import { formatReportMoney, reportCurrency, servedRegionsOverlap } from '@evi/shared';
import type { AgentInviteStatus, AgentRow, PartnerHistoryKind, PartnerProfile } from './api';

/**
 * Sales Partner directory (Partners > Sales Partners, Warren, 24 October
 * 2026): the list's search and filters, the recruit link and the profile's
 * tabs, as plain functions so they are tested without a browser.
 */

/** The invite pill's state, the rule of deriveInviteStatus in api.ts (kept
 *  here so this module loads without the Api client). */
export function inviteStateOf(a: { invite_accepted_at?: string | null; invite_token_expires_at?: string | null }, now = Date.now()): AgentInviteStatus {
  if (a.invite_accepted_at) return 'accepted';
  if (a.invite_token_expires_at && new Date(a.invite_token_expires_at).getTime() > now) return 'pending';
  return 'expired';
}

/** Payout setup from the stored snapshot (GET /admin/agents connect_state). */
export type PartnerConnectState = 'not_started' | 'unchecked' | 'pending' | 'ready' | 'restricted';
export const CONNECT_STATE_LABELS: Record<PartnerConnectState, string> = {
  not_started: 'Not started',
  unchecked: 'Account created',
  pending: 'Pending',
  ready: 'Ready',
  restricted: 'Needs information',
};

/** A list row: an Api from before this change sends no connect_state, so the
 *  account id decides as the old list did. */
export function connectStateOf(row: Pick<AgentRow, 'stripe_connect_account_id'> & { connect_state?: PartnerConnectState | null }): PartnerConnectState {
  if (row.connect_state) return row.connect_state;
  return row.stripe_connect_account_id ? 'unchecked' : 'not_started';
}

export interface PartnerFilters {
  search: string;
  status: 'all' | 'active' | 'inactive';
  program: 'all' | 'standard' | 'golden';
  invite: 'all' | AgentInviteStatus;
  connect: 'all' | PartnerConnectState;
  /** 'all', 'house' (no rep), or a sales staff id. */
  rep: string;
  /** Region codes (25 October 2026): a partner serving ANY of them matches;
   *  empty is every partner. */
  regions: string[];
}
export const DEFAULT_PARTNER_FILTERS: PartnerFilters = {
  search: '', status: 'all', program: 'all', invite: 'all', connect: 'all', rep: 'all', regions: [],
};

/** How many filters (search excluded) differ from the default. */
export function activeFilterCount(f: PartnerFilters): number {
  return (['status', 'program', 'invite', 'connect', 'rep'] as const).filter((k) => f[k] !== DEFAULT_PARTNER_FILTERS[k]).length
    + (f.regions.length ? 1 : 0);
}

const digits = (v: string | null | undefined): string => (v ?? '').replace(/\D/g, '');

/** Search across name (the company), contact name, email, phone, the
 *  partner's referral codes and its id. Phone matches on digits, so
 *  "(416) 555" finds +14165550100. */
export function partnerMatchesSearch(row: AgentRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const text = [row.name, row.contact_name, row.email, row.id, ...(row.referral_codes ?? [])]
    .filter((v): v is string => !!v).map((v) => v.toLowerCase());
  if (text.some((v) => v.includes(q))) return true;
  const qd = digits(q);
  return qd.length >= 3 && digits(row.phone).includes(qd);
}

export function filterPartners(rows: AgentRow[], f: PartnerFilters): AgentRow[] {
  return rows.filter((a) => {
    if (f.status === 'active' && !a.active) return false;
    if (f.status === 'inactive' && a.active) return false;
    if (f.program !== 'all' && (a.program_info?.program ?? 'standard') !== f.program) return false;
    if (f.invite !== 'all' && inviteStateOf(a) !== f.invite) return false;
    if (f.connect !== 'all' && connectStateOf(a) !== f.connect) return false;
    if (f.rep === 'house' && a.sales_staff_id) return false;
    if (f.rep !== 'all' && f.rep !== 'house' && a.sales_staff_id !== f.rep) return false;
    if (f.regions.length && !servedRegionsOverlap(a.served_regions ?? [], f.regions)) return false;
    return partnerMatchesSearch(a, f.search);
  });
}

/** The reps present in the rows, for the rep filter, by name. */
export function repOptions(rows: AgentRow[]): Array<{ id: string; name: string }> {
  const seen = new Map<string, string>();
  for (const r of rows) if (r.sales_staff_id) seen.set(r.sales_staff_id, r.sales_rep_name || 'Sales staff');
  return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

// ── Recruit link ────────────────────────────────────────────────────

/** The label every recruit link carries. POST /admin/sales-flow/campaigns
 *  keeps one live code per (kind, rep, label), so asking again returns the
 *  same link rather than minting another. */
export const RECRUIT_LINK_LABEL = 'EVI recruit link';

export interface RecruitLinkRow {
  id: string; kind: 'house' | 'campaign'; label: string; staff_id: string | null; staff_name: string | null;
  created_at: string; url: string; apply_url?: string;
}

/** The live recruit link for a choice: 'house' (no rep) or a sales staff id. */
export function recruitLinkFor(rows: RecruitLinkRow[], credit: string): RecruitLinkRow | null {
  return rows.find((r) => r.label === RECRUIT_LINK_LABEL
    && (credit === 'house' ? r.kind === 'house' && !r.staff_id : r.kind === 'campaign' && r.staff_id === credit)) ?? null;
}

/** The body that issues (or, without rotate, returns) that link. */
export function recruitLinkRequest(credit: string, rotate = false): { kind: 'house' | 'campaign'; label: string; staff_id?: string; rotate: boolean } {
  return credit === 'house'
    ? { kind: 'house', label: RECRUIT_LINK_LABEL, rotate }
    : { kind: 'campaign', label: RECRUIT_LINK_LABEL, staff_id: credit, rotate };
}

/** The link a prospect opens: the application form itself. An Api from
 *  before apply_url sends only url, the same page without the form anchor. */
export function recruitApplyUrl(row: Pick<RecruitLinkRow, 'url' | 'apply_url'>): string {
  if (row.apply_url) return row.apply_url;
  return row.url.includes('#') ? row.url : `${row.url}#start`;
}

// ── Profile ─────────────────────────────────────────────────────────

export const PARTNER_TABS = ['details', 'jobs', 'leads', 'chargers', 'commissions', 'statements', 'activity'] as const;
export type PartnerTab = typeof PARTNER_TABS[number];
export const PARTNER_TAB_LABELS: Record<PartnerTab, string> = {
  details: 'Details',
  jobs: 'Jobs',
  leads: 'Leads',
  chargers: 'Chargers',
  commissions: 'Commissions and payouts',
  statements: 'Statements',
  activity: 'Activity',
};

const ALL_HISTORY_KINDS: PartnerHistoryKind[] = ['jobs', 'installs', 'leads', 'chargers', 'sites', 'payouts', 'ledger', 'statements',
  'team_statements', 'activity', 'mail', 'deliverability'];

/** The history sources this viewer reads. An Api from before 25 October 2026
 *  sends no history_kinds: then every source for a Super Admin, none else. */
export function historyKindsOf(c: Pick<PartnerProfile['capabilities'], 'history' | 'history_kinds'>): PartnerHistoryKind[] {
  return c.history_kinds ?? (c.history ? ALL_HISTORY_KINDS : []);
}

/** EVI-internal details (rep, application, referral link, team, Stripe ids). */
export function showsInternal(c: Pick<PartnerProfile['capabilities'], 'history' | 'internal'>): boolean {
  return c.internal ?? c.history;
}

/** Each tab's count from the profile counts, only from sources this viewer
 *  reads (a source it does not read is null). */
export function partnerTabCounts(counts: PartnerProfile['counts']): Partial<Record<PartnerTab, number>> {
  if (!counts) return {};
  const sum = (...v: Array<number | null | undefined>) => (v.some((n) => typeof n === 'number') ? v.reduce<number>((t, n) => t + (n ?? 0), 0) : undefined);
  const out: Partial<Record<PartnerTab, number>> = {};
  const set = (tab: PartnerTab, n: number | undefined) => { if (n !== undefined) out[tab] = n; };
  set('jobs', sum(counts.jobs, counts.installs));
  set('leads', sum(counts.leads));
  set('chargers', sum(counts.chargers, counts.sites));
  set('commissions', sum(counts.payouts));
  set('activity', sum(counts.activity, counts.mail));
  return out;
}

/** The tab to open: the one asked for when the Api allows it, else Details. */
export function initialPartnerTab(requested: string | null | undefined, allowed: readonly string[]): PartnerTab {
  const tab = PARTNER_TABS.find((t) => t === requested);
  return tab && allowed.includes(tab) ? tab : 'details';
}

/** Money in its own currency, never a default one. A row with no recorded
 *  currency keeps its amount and says so, as formatCommissionRow does. */
export function partnerMoney(cents: number | null | undefined, currency: string | null | undefined): string {
  const value = cents ?? 0;
  return reportCurrency(currency) ? formatReportMoney(value, currency, 'en-US') : `${(value / 100).toFixed(2)} (currency not recorded)`;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'Not recorded';
  const d = new Date(iso);
  return Number.isFinite(+d) ? d.toLocaleDateString() : 'Not recorded';
}
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return 'Not recorded';
  const d = new Date(iso);
  return Number.isFinite(+d) ? d.toLocaleString() : 'Not recorded';
}

/** Words for a recorded value such as 'pending_stripe_setup'. */
export function humanize(value: string | null | undefined): string {
  if (!value) return 'Not recorded';
  const s = value.replace(/[_.]+/g, ' ').trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Basis points as a percentage: 350 -> "3.50%". */
export function bpsPercent(bps: number | null | undefined): string {
  return typeof bps === 'number' ? `${(bps / 100).toFixed(2)}%` : 'Not recorded';
}

/**
 * The partner's program rate for Admin (sales comp change, 27 October 2026): what a new qualifying launch is stamped with today, from the
 * Api's program view, and for Golden the next step. The stored default_commission_bps is a reference value that never pays, so Admin no
 * longer shows it as a rate.
 */
export function programRateLabel(view: { program: 'standard' | 'golden'; current_rate_bps: number; next_launch_rate_bps?: number; next_tier: { count: number; rate_bps: number } | null } | null | undefined): string {
  if (!view) return 'Not available';
  if (view.program !== 'golden') return `${bpsPercent(view.current_rate_bps)} on every qualifying charger`;
  // The next launch's own rate, not the last launched charger's: at a boundary (49, 99, 149) they differ.
  const nextLaunch = view.next_launch_rate_bps ?? view.current_rate_bps;
  const now = `${bpsPercent(nextLaunch)} on new qualifying launches`;
  return view.next_tier && view.next_tier.rate_bps !== nextLaunch ? `${now}; ${bpsPercent(view.next_tier.rate_bps)} from charger ${view.next_tier.count}` : now;
}
